import RecordLabel from "components/RecordLabel";
import Tooltip from "components/Tooltip/Tooltip";
import {
    CloseAllActionButton,
    createMenuChildren,
} from "containers/Projects/components/ProjectTree/createChildrenMenu";
import { propagateScrollToSidebar } from "containers/Projects/components/ProjectTree/ProjectTree";
import { RefObject } from "react";
import { MenuItem, SubMenu } from "react-pro-sidebar";
import { useDispatch } from "react-redux";
import { ParentReference, ProjectQuickAccessEntry } from "./interfaces";

interface LatestListContainerProps {
    pinnedList?: ProjectQuickAccessEntry[];
    openedList?: ProjectQuickAccessEntry[];
    containerRef: RefObject<HTMLDivElement>;
    containerLabel: string;
    isContainerOpened: boolean;
    setContainerOpen: (value: boolean) => void;
    isItemCurrentlyViewed: (item: ProjectQuickAccessEntry) => boolean;
    resourceType: "bundle" | "project" | "workspace";
    id: string;
    onCloseIconClick: (item: ProjectQuickAccessEntry, parent?: ParentReference) => Promise<any>;
}

export const LatestListContainer = ({
    pinnedList,
    openedList,
    containerRef,
    containerLabel,
    isContainerOpened,
    setContainerOpen,
    isItemCurrentlyViewed,
    resourceType,
    id,
    onCloseIconClick,
}: LatestListContainerProps) => {
    const dispatch = useDispatch();
    const highlightActions = (_, className) => {
        if (containerRef) {
            const actionList = containerRef.current?.querySelectorAll(`.${className}`);
            actionList?.forEach((action) => {
                (action as any).classList.add("closeSingleActionHover");
            });
        }
    };

    const highlightSingleCloseActions = (event) => highlightActions(event, "closeSingleAction");
    const highlightSingleCloseUnpinnedActions = (event) =>
        highlightActions(event, "closeUnpinnedSingleAction");

    const removeHighlightSingleCloseActions = () => {
        const closeSingleActionList = document.querySelectorAll(".closeSingleActionHover");
        closeSingleActionList.forEach((action) => {
            (action as any).classList.remove("closeSingleActionHover");
        });
    };
    const disableSingleActions = (className) => {
        if (containerRef) {
            const actionList = containerRef.current?.querySelectorAll(`.${className} button`);
            actionList?.forEach((action) => {
                (action as any).classList.add("closeSingleActionDisabled");
                (action as any).setAttribute("disabled", "");
            });
        }
    };

    const disableSingleCloseActions = () => disableSingleActions("closeSingleAction");
    const disableSingleCloseUnpinnedActions = () =>
        disableSingleActions("closeUnpinnedSingleAction");

    const enableSingleCloseActions = () => {
        const closeSingleActionList = document.querySelectorAll(".closeSingleActionDisabled");
        closeSingleActionList.forEach((action) => {
            (action as any).classList.remove("closeSingleActionDisabled");
            (action as any).removeAttribute("disabled");
        });
    };

    const allRecords = [...(openedList ?? []), ...(pinnedList ?? [])];
    const openedRecords = openedList ?? [];

    const filterCurrentlyViewedItems = (items: ProjectQuickAccessEntry[]) =>
        items.filter((item) => !isItemCurrentlyViewed(item));

    const openedRecordsToClose = filterCurrentlyViewedItems(openedRecords);
    const allRecordsToClose = filterCurrentlyViewedItems(allRecords);

    const innerContent = (
        <>
            {openedRecords?.length ? (
                <Tooltip
                    overlay="Close all unpinned"
                    key="closeAllUnpinned"
                    overlayClassName="closeAllTooltip"
                    showArrow={false}
                    align={{ offset: [0, -5] }}
                >
                    <CloseAllActionButton
                        itemList={openedRecordsToClose}
                        action={onCloseIconClick}
                        actionIconClassName="closeAction closeAllAction closeAllUnpinnedAction"
                        onMouseEnter={highlightSingleCloseUnpinnedActions}
                        onMouseLeave={removeHighlightSingleCloseActions}
                        enableSingleActions={enableSingleCloseActions}
                        disableSingleActions={disableSingleCloseUnpinnedActions}
                    />
                </Tooltip>
            ) : null}
            {allRecords?.length ? (
                <Tooltip
                    overlay="Close all"
                    key="closeAll"
                    overlayClassName="closeAllTooltip"
                    showArrow={false}
                    align={{ offset: [0, -5] }}
                >
                    <CloseAllActionButton
                        itemList={allRecordsToClose}
                        action={(item) => {
                            return onCloseIconClick(item);
                        }}
                        actionIconClassName="closeAction closeAllAction"
                        onMouseEnter={highlightSingleCloseActions}
                        onMouseLeave={removeHighlightSingleCloseActions}
                        enableSingleActions={enableSingleCloseActions}
                        disableSingleActions={disableSingleCloseActions}
                    />
                </Tooltip>
            ) : null}
        </>
    );
    return pinnedList?.length || openedList?.length ? (
        <MenuItem key={containerLabel} ref={containerRef}>
            <ul className="latestSubMenu">
                <SubMenu
                    title={
                        (
                            <RecordLabel
                                name={containerLabel}
                                scrollIntoView
                                tooltipOverlayClassName={propagateScrollToSidebar}
                                tooltipOverlay={
                                    <div className="projectTreeTooltipOverlay">
                                        {containerLabel}
                                        {innerContent}
                                    </div>
                                }
                            >
                                {innerContent}
                            </RecordLabel>
                        ) as any
                    }
                    open={isContainerOpened}
                    onOpenChange={(open) => setContainerOpen(open)}
                >
                    {createMenuChildren({
                        items: pinnedList,
                        action: (item) => {
                            const parent: ParentReference | undefined = isItemCurrentlyViewed(item)
                                ? { recordType: resourceType, id }
                                : undefined;
                            return onCloseIconClick(item, parent);
                        },
                        actionIconClassName: "closeAction closeSingleAction",
                        itemClassName: "pinnedItem",
                        dispatch,
                    })}
                    {createMenuChildren({
                        items: openedList,
                        action: (item) => {
                            const parent: ParentReference | undefined = isItemCurrentlyViewed(item)
                                ? { recordType: resourceType, id }
                                : undefined;
                            return onCloseIconClick(item, parent);
                        },
                        actionIconClassName:
                            "closeAction closeSingleAction closeUnpinnedSingleAction",
                        dispatch,
                    })}
                </SubMenu>
            </ul>
        </MenuItem>
    ) : null;
};
